/* Mock profile + addresses CRUD.
 *
 * Profile is stored as a patch under `profile.<user_id>`. On read we merge
 * the base demo user from data/mocks/users.js with the patch. This lets the
 * Demo Reset button cleanly restore everyone to their seed identity.
 *
 * Addresses live under the shared `addresses` key — every demo role shares
 * the same address book for the demo. Per-user scoping is a Phase M6 nicety.
 */

import * as store from "@/data/mocks/storage";
import { DEMO_USERS } from "@/data/mocks/users";
import { withLatency, withError } from "./_util";

const PROFILE_DOMAIN = (user_id) => `profile.${user_id}`;
const ADDR_DOMAIN = "addresses";

function findBaseUser(user_id) {
  return DEMO_USERS.find((u) => u.id === user_id) || null;
}

function getCurrentSession() {
  return store.get("session", null);
}

function mergeProfile(user_id) {
  const base = findBaseUser(user_id);
  if (!base) return null;
  const patch = store.get(PROFILE_DOMAIN(user_id), {}) || {};
  return {
    id: base.id,
    email: base.email,
    role: base.role,
    status: base.status,
    phone: patch.phone ?? base.phone,
    dob: patch.dob ?? null,
    full_name: patch.full_name ?? base.profile.full_name,
    avatar_data_url: patch.avatar_data_url ?? null,
    permissions: base.permissions,
    email_verified: base.email_verified,
    attributes: { ...(base.profile.attributes || {}), ...(patch.attributes || {}) },
  };
}

export async function get() {
  const session = getCurrentSession();
  if (!session) return withError("Not authenticated", 401);
  const profile = mergeProfile(session.user_id);
  if (!profile) return withError("User not found", 404);
  return withLatency(profile);
}

export async function update(patch = {}) {
  const session = getCurrentSession();
  if (!session) return withError("Not authenticated", 401);
  const current = store.get(PROFILE_DOMAIN(session.user_id), {}) || {};
  const next = { ...current };
  const allowed = ["full_name", "phone", "dob", "avatar_data_url"];
  for (const k of allowed) {
    if (patch[k] !== undefined) next[k] = patch[k];
  }
  store.set(PROFILE_DOMAIN(session.user_id), next);
  return withLatency(mergeProfile(session.user_id));
}

/* ----- Addresses ----- */

function readAddresses() {
  return store.get(ADDR_DOMAIN, []) || [];
}

function writeAddresses(arr) {
  store.set(ADDR_DOMAIN, arr);
}

export async function addresses_list() {
  return withLatency(readAddresses());
}

export async function addresses_get(id) {
  const found = readAddresses().find((a) => a.id === id);
  if (!found) return withError("Address not found", 404);
  return withLatency(found);
}

export async function addresses_create(payload = {}) {
  const list = readAddresses();
  const next = {
    id: "a-" + Math.random().toString(36).slice(2, 8),
    label: payload.label || "Address",
    line1: payload.line1 || "",
    line2: payload.line2 || "",
    city: payload.city || "",
    state: payload.state || "MH",
    pin: payload.pin || "",
    phone: payload.phone || "",
    type: payload.type || "Business",
    default: payload.default ?? list.length === 0,
  };
  // If this one is marked default, clear others.
  let updated = list;
  if (next.default) {
    updated = list.map((a) => ({ ...a, default: false }));
  }
  writeAddresses([next, ...updated]);
  return withLatency(next);
}

export async function addresses_update(id, patch = {}) {
  const list = readAddresses();
  const idx = list.findIndex((a) => a.id === id);
  if (idx === -1) return withError("Address not found", 404);
  const merged = { ...list[idx], ...patch, id };
  let next = list.slice();
  // If patch makes this default, clear others.
  if (patch.default) {
    next = next.map((a) => ({ ...a, default: false }));
  }
  next[idx] = merged;
  writeAddresses(next);
  return withLatency(merged);
}

export async function addresses_delete(id) {
  const list = readAddresses();
  const removed = list.find((a) => a.id === id);
  if (!removed) return withError("Address not found", 404);
  let next = list.filter((a) => a.id !== id);
  // If we removed the default, promote the first remaining.
  if (removed.default && next.length > 0) {
    next = next.map((a, i) => ({ ...a, default: i === 0 }));
  }
  writeAddresses(next);
  return withLatency({ ok: true });
}

export async function addresses_setDefault(id) {
  const list = readAddresses();
  if (!list.find((a) => a.id === id)) return withError("Address not found", 404);
  const next = list.map((a) => ({ ...a, default: a.id === id }));
  writeAddresses(next);
  return withLatency(next.find((a) => a.id === id));
}
